import { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, List, KanbanSquare, Table2, Calculator as CalcIcon, Loader2, Plus, X, Trash2, MessageSquare, Link2, Pencil, Users, Check } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";
import DatePicker from "../../components/os/DatePicker";

const ease = [0.16, 1, 0.3, 1];
const STATUSES = ["nuevo", "en_progreso", "en_revision", "completado", "archivado"];
const STATUS_LABEL = { nuevo: "Nuevo", en_progreso: "En progreso", en_revision: "En revisión", completado: "Completado", archivado: "Archivado" };
const PRIO_LABEL = { baja: "Baja", media: "Media", alta: "Alta" };
const PRIO_BADGE = { baja: "badge-muted", media: "badge-violet", alta: "badge-amber" };
const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet transition-colors text-sm";

// ---- Formateo por tipo de campo ----
const fmtCurrency = (v) => { const n = Number(v); return isNaN(n) ? (v || "—") : n.toLocaleString("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }); };
const fmtNum = (v) => { const n = Number(v); return isNaN(n) ? v : n.toLocaleString("es-MX"); };
function fmtField(field, val) {
  if (val == null || val === "") return "—";
  if (field.type === "currency") return fmtCurrency(val);
  if (field.type === "percent") return `${val}%`;
  if (field.type === "number") return fmtNum(val);
  if (field.type === "select") { const o = (field.options || []).find((o) => String(o.value) === String(val)); return o ? o.label : val; }
  return String(val);
}

// ---- Calculadoras reales ----
function computeCalc(id, v) {
  const num = (k) => Number(v[k] || 0);
  if (id === "mortgage") {
    const precio = num("precio"), engMonto = precio * num("enganche_pct") / 100, loan = precio - engMonto;
    const r = num("tasa_anual") / 100 / 12, n = num("plazo_anios") * 12;
    const pago = r > 0 && n > 0 ? loan * r / (1 - Math.pow(1 + r, -n)) : (n > 0 ? loan / n : 0);
    const total = pago * n;
    return { rows: [{ label: "Enganche", value: fmtCurrency(engMonto) }, { label: "Monto a financiar", value: fmtCurrency(loan) }, { label: "Mensualidad", value: fmtCurrency(pago), hl: true }, { label: "Total a pagar", value: fmtCurrency(total) }, { label: "Intereses", value: fmtCurrency(total - loan) }], primary: fmtCurrency(pago), primaryLabel: "Mensualidad estimada" };
  }
  if (id === "commission") {
    const c = num("monto_venta") * num("tasa_comision") / 100 + num("bono");
    return { rows: [{ label: "Comisión base", value: fmtCurrency(num("monto_venta") * num("tasa_comision") / 100) }, { label: "Bono", value: fmtCurrency(num("bono")) }, { label: "Comisión total", value: fmtCurrency(c), hl: true }], primary: fmtCurrency(c), primaryLabel: "Comisión total" };
  }
  if (id === "quote") {
    const sub = num("cantidad") * num("precio_unitario"), desc = sub * num("descuento_pct") / 100, base = sub - desc, imp = base * num("impuesto_pct") / 100, total = base + imp;
    return { rows: [{ label: "Subtotal", value: fmtCurrency(sub) }, { label: "Descuento", value: fmtCurrency(desc) }, { label: "Base", value: fmtCurrency(base) }, { label: "Impuesto", value: fmtCurrency(imp) }, { label: "Total", value: fmtCurrency(total), hl: true }], primary: fmtCurrency(total), primaryLabel: "Total" };
  }
  if (id === "roi") {
    const inv = num("inversion"), ret = num("retorno"), g = ret - inv, roi = inv > 0 ? g / inv * 100 : 0;
    return { rows: [{ label: "Inversión", value: fmtCurrency(inv) }, { label: "Ganancia neta", value: fmtCurrency(g) }, { label: "ROI", value: `${roi.toFixed(1)}%`, hl: true }], primary: `${roi.toFixed(1)}%`, primaryLabel: "Retorno de inversión" };
  }
  return { rows: [], primary: "", primaryLabel: "" };
}

function Modal({ title, onClose, children, testid, wide }) {
  useEffect(() => { const h = (e) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }} className={`card-surface w-full ${wide ? "max-w-lg" : "max-w-md"} max-h-[90vh] overflow-y-auto p-6`}>
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </motion.div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type === "textarea") return <textarea className={input} rows={3} placeholder={field.help || field.label} value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
  if (field.type === "select") return <SelectField testid={`field-${field.key}`} value={value ? String(value) : undefined} placeholder={`Selecciona ${field.label.toLowerCase()}`} options={field.options || []} onChange={onChange} />;
  if (field.type === "date") return <DatePicker testid={`field-${field.key}`} value={value || ""} onChange={onChange} placeholder={field.label} />;
  const t = ["number", "currency", "percent"].includes(field.type) ? "number" : "text";
  return <input type={t} step="any" className={input} placeholder={field.help || field.label} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={`field-${field.key}`} />;
}

function DynamicFields({ fields, values, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {fields.map((f) => (
        <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
          <label className="text-xs text-navy/50 mb-1 block">{f.label}{f.required && <span className="text-violet"> *</span>}</label>
          <FieldInput field={f} value={values[f.key]} onChange={(v) => onChange({ ...values, [f.key]: v })} />
        </div>
      ))}
    </div>
  );
}

export default function AppWorkspace() {
  const { appId } = useParams();
  const [meta, setMeta] = useState(null);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ title: "", description: "", status: "nuevo", priority: "media", project_id: "", fields: {} });

  const schema = meta?.schema;
  const label = schema?.record_label || "Registro";
  const labelPlural = schema?.record_label_plural || "Registros";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([api.get(`/workspace/${appId}/records`), api.get(`/workspace/${appId}/summary`)]);
      setRecords(r.data); setSummary(s.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [appId]);

  useEffect(() => {
    setTab(null); setMeta(null);
    api.get(`/catalog/app/${appId}`).then((r) => setMeta(r.data)).catch(() => setMeta({ app: appId, studio: "", suite: "OMNIA" }));
    api.get("/workspace/shared/projects").then((r) => setProjects(r.data)).catch(() => {});
    load();
  }, [appId, load]);

  // Fija la pestaña principal según el arquetipo cuando llega el esquema
  useEffect(() => {
    if (schema && tab === null) {
      const map = { list: "lista", table: "tabla", board: "tablero", panel: "panel", calculator: "calculadora" };
      setTab(map[schema.primary_view] || "lista");
    }
  }, [schema, tab]);

  const submit = async () => {
    if (!f.title.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      await api.post(`/workspace/${appId}/records`, { title: f.title, description: f.description, status: f.status, priority: f.priority, project_id: f.project_id || null, fields: f.fields });
      toast.success(`${label} creado`); setOpen(false);
      setF({ title: "", description: "", status: "nuevo", priority: "media", project_id: "", fields: {} }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  const setStatus = async (rec, status) => { try { await api.patch(`/workspace/${appId}/records/${rec.id}`, { status }); load(); if (detail?.id === rec.id) setDetail({ ...detail, status }); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/workspace/${appId}/records/${id}`); toast.success("Eliminado"); setDetail(null); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };

  const kpis = summary ? [
    { label: labelPlural, value: summary.total }, { label: "Abiertos", value: summary.open },
    { label: "Completados", value: summary.done }, { label: "Prioridad alta", value: summary.high },
  ] : [];

  const cols = schema?.columns || ["title"];
  const colDef = (key) => key === "title" ? { key: "title", label: "Nombre", type: "text" } : (schema?.fields || []).find((x) => x.key === key) || { key, label: key, type: "text" };
  const cellVal = (rec, key) => key === "title" ? rec.title : fmtField(colDef(key), rec.fields?.[key]);

  const tabs = useMemo(() => {
    const t = [["lista", "Lista", List], ["tabla", "Tabla", Table2], ["tablero", "Tablero", KanbanSquare], ["panel", "Panel", LayoutGrid]];
    if (schema?.calculator) t.push(["calculadora", schema.calculator.title || "Calculadora", CalcIcon]);
    return t;
  }, [schema]);

  return (
    <OSLayout title={meta?.app || appId}>
      <div className="space-y-8" data-testid="app-workspace">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
          <div className="overline text-navy/40 mb-2 flex items-center gap-2"><LayoutGrid className="w-3.5 h-3.5" /> {meta?.suite} {meta?.studio ? `· ${meta.studio}` : ""}</div>
          <h2 className="font-editorial text-4xl md:text-5xl tracking-tight" data-testid="app-workspace-title">{meta?.app || appId}</h2>
          <p className="mt-2 text-navy/50 max-w-2xl" data-testid="app-workspace-desc">{schema?.description || "Espacio de trabajo OMNIA."}</p>
        </motion.div>

        {schema?.capabilities?.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease, delay: 0.1 }} className="card-surface p-5" data-testid="app-capabilities">
            <div className="overline text-navy/40 mb-3">¿Qué hace esta app?</div>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
              {schema.capabilities.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-navy/70"><span className="text-violet mt-1.5 w-1 h-1 rounded-full bg-violet shrink-0" />{c}</div>
              ))}
            </div>
          </motion.div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-2 border-b border-navy/10 pb-1 flex-wrap">
            {tabs.map(([k, l, Ic]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`app-tab-${k}`} className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm transition-colors ${tab === k ? "bg-gradient-violet text-white" : "text-navy/55 hover:text-navy hover:bg-navy/5"}`}><Ic className="w-3.5 h-3.5" /> {l}</button>
            ))}
          </div>
          <button onClick={() => setOpen(true)} className="btn btn-primary" data-testid="app-new-record-btn"><Plus className="w-4 h-4" /> Nuevo: {label}</button>
        </div>

        {loading || !schema ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : <>
          {tab === "calculadora" && schema.calculator && (
            <Calculator calc={schema.calculator} label={label} appId={appId} onSaved={load} />
          )}

          {tab === "panel" && (
            <div className="space-y-6" data-testid="app-panel">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {kpis.map((k) => (<div key={k.label} className="card-surface p-5"><div className="overline text-navy/40">{k.label}</div><div className="mt-3 text-2xl font-medium tracking-tight">{k.value}</div></div>))}
              </div>
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="card-surface p-6">
                  <div className="overline text-navy/40 mb-4">Distribución por estado</div>
                  <div className="space-y-3">
                    {summary && Object.entries(summary.by_status).map(([st, n]) => {
                      const max = Math.max(1, ...Object.values(summary.by_status));
                      return (<div key={st} className="flex items-center gap-3" data-testid={`app-panel-status-${st}`}>
                        <div className="w-24 text-sm text-navy/60 shrink-0">{STATUS_LABEL[st]}</div>
                        <div className="flex-1 h-2.5 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${(n / max) * 100}%` }} /></div>
                        <div className="w-7 text-right text-sm text-navy/50">{n}</div>
                      </div>);
                    })}
                  </div>
                </div>
                <div className="card-surface p-6">
                  <div className="overline text-navy/40 mb-4">Por prioridad</div>
                  <div className="space-y-3">
                    {["alta", "media", "baja"].map((p) => {
                      const n = records.filter((r) => r.priority === p).length;
                      const max = Math.max(1, records.length);
                      return (<div key={p} className="flex items-center gap-3" data-testid={`app-panel-prio-${p}`}>
                        <div className="w-24 text-sm text-navy/60 shrink-0">{PRIO_LABEL[p]}</div>
                        <div className="flex-1 h-2.5 rounded-full bg-navy/10 overflow-hidden"><div className="h-full bg-gradient-violet" style={{ width: `${(n / max) * 100}%` }} /></div>
                        <div className="w-7 text-right text-sm text-navy/50">{n}</div>
                      </div>);
                    })}
                  </div>
                </div>
                <div className="card-surface p-6">
                  <div className="overline text-navy/40 mb-4">Actividad reciente</div>
                  <div className="space-y-2 max-h-56 overflow-y-auto">
                    {records.length === 0 ? <div className="text-sm text-navy/40">Sin actividad.</div> : records.slice(0, 6).map((r) => (
                      <button key={r.id} onClick={() => setDetail(r)} className="w-full text-left flex items-center justify-between gap-2 py-1.5 border-b border-navy/5 last:border-0 hover:text-violet transition-colors" data-testid={`app-panel-recent-${r.id}`}>
                        <span className="text-sm truncate">{r.title}</span>
                        <span className="badge badge-violet shrink-0">{STATUS_LABEL[r.status]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "lista" && (
            records.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="app-empty">Aún no hay {labelPlural.toLowerCase()}. Crea el primero.</div>
            : <div className="space-y-2" data-testid="app-list">
                {records.map((r) => (
                  <button key={r.id} onClick={() => setDetail(r)} data-testid={`app-record-${r.id}`} className="w-full text-left card-surface p-4 flex flex-wrap items-center gap-3 justify-between hover:border-violet/40 transition-colors">
                    <div className="min-w-0"><div className="font-medium truncate">{r.title}</div><div className="text-xs text-navy/50 truncate">{r.description || cols.filter((c) => c !== "title").map((c) => `${colDef(c).label}: ${cellVal(r, c)}`).join(" · ") || "—"}</div></div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`badge ${PRIO_BADGE[r.priority]}`}>{PRIO_LABEL[r.priority]}</span>
                      <span className="badge badge-violet">{STATUS_LABEL[r.status]}</span>
                    </div>
                  </button>
                ))}
              </div>
          )}

          {tab === "tabla" && (
            records.length === 0 ? <div className="card-surface py-16 text-center text-navy/40 text-sm" data-testid="app-table-empty">Sin {labelPlural.toLowerCase()}.</div>
            : <div className="card-surface overflow-x-auto" data-testid="app-table">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-navy/10 text-navy/50">
                    {cols.map((c) => <th key={c} className="text-left font-medium px-4 py-3 whitespace-nowrap">{colDef(c).label}</th>)}
                    <th className="text-left font-medium px-4 py-3">Estado</th>
                  </tr></thead>
                  <tbody>
                    {records.map((r) => (
                      <tr key={r.id} onClick={() => setDetail(r)} data-testid={`app-row-${r.id}`} className="border-b border-navy/5 last:border-0 hover:bg-navy/[0.03] cursor-pointer">
                        {cols.map((c) => <td key={c} className="px-4 py-3 whitespace-nowrap">{cellVal(r, c)}</td>)}
                        <td className="px-4 py-3"><span className="badge badge-violet">{STATUS_LABEL[r.status]}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
          )}

          {tab === "tablero" && (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="app-board">
              {STATUSES.map((st) => (
                <div key={st} className="bg-navy/[0.03] rounded-lg p-3 min-h-[120px]" data-testid={`app-col-${st}`}>
                  <div className="overline text-navy/40 mb-2">{STATUS_LABEL[st]}</div>
                  <div className="space-y-2">
                    {records.filter((r) => r.status === st).map((r) => (
                      <button key={r.id} onClick={() => setDetail(r)} className="w-full text-left card-surface p-2.5 text-sm hover:border-violet/40 transition-colors" data-testid={`app-card-${r.id}`}>
                        <div className="truncate font-medium">{r.title}</div>
                        {cols.filter((c) => c !== "title")[0] && <div className="text-xs text-navy/50 truncate mt-0.5">{cellVal(r, cols.filter((c) => c !== "title")[0])}</div>}
                        <span className={`badge ${PRIO_BADGE[r.priority]} mt-1`}>{PRIO_LABEL[r.priority]}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>}
      </div>

      {open && schema && <Modal title={`Nuevo: ${label}`} onClose={() => setOpen(false)} testid="app-record-dialog" wide>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-navy/50 mb-1 block">Nombre / Título <span className="text-violet">*</span></label>
            <input className={input} placeholder={`Nombre de ${label.toLowerCase()}`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="app-field-title" />
          </div>
          {schema.fields?.length > 0 && <DynamicFields fields={schema.fields} values={f.fields} onChange={(fields) => setF({ ...f, fields })} />}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs text-navy/50 mb-1 block">Estado</label><SelectField value={f.status} options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))} onChange={(v) => setF({ ...f, status: v })} /></div>
            <div><label className="text-xs text-navy/50 mb-1 block">Prioridad</label><SelectField value={f.priority} options={Object.keys(PRIO_LABEL).map((p) => ({ value: p, label: PRIO_LABEL[p] }))} onChange={(v) => setF({ ...f, priority: v })} /></div>
          </div>
          <div><label className="text-xs text-navy/50 mb-1 block">Vincular a proyecto (opcional)</label>
            <SelectField testid="app-field-project" value={f.project_id || "none"} onChange={(v) => setF({ ...f, project_id: v === "none" ? "" : v })}
              options={[{ value: "none", label: "Sin proyecto" }, ...projects.map((p) => ({ value: p.id, label: `${p.source}: ${p.name}` }))]} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={submit} disabled={saving} className="btn btn-primary" data-testid="app-record-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
      </Modal>}

      {detail && schema && <RecordDetail appId={appId} rec={detail} schema={schema} onClose={() => setDetail(null)} onStatus={setStatus} onDelete={del} onSaved={load} projects={projects} />}
    </OSLayout>
  );
}

function Calculator({ calc, label, appId, onSaved }) {
  const [v, setV] = useState({});
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const res = computeCalc(calc.id, v);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/workspace/${appId}/records`, { title: name || `${calc.title} — ${new Date().toLocaleDateString("es-MX")}`, fields: { ...v, resultado: res.primary } });
      toast.success(`${label} guardado`); setName(""); setV({}); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="app-calculator">
      <div className="card-surface p-6 space-y-4">
        <div className="overline text-navy/40">{calc.title}</div>
        <DynamicFields fields={calc.inputs} values={v} onChange={setV} />
        <div>
          <label className="text-xs text-navy/50 mb-1 block">Nombre para guardar</label>
          <input className={input} placeholder="Ej. Cliente / referencia" value={name} onChange={(e) => setName(e.target.value)} data-testid="calc-name" />
        </div>
        <button onClick={save} disabled={saving} className="btn btn-primary w-full" data-testid="calc-save"><Check className="w-4 h-4" /> Guardar resultado</button>
      </div>
      <div className="card-surface p-6 flex flex-col" data-testid="calc-result">
        <div className="overline text-navy/40 mb-1">{res.primaryLabel}</div>
        <div className="font-editorial text-4xl tracking-tight text-violet mb-6" data-testid="calc-primary">{res.primary || "—"}</div>
        <div className="space-y-2">
          {res.rows.map((r, i) => (
            <div key={i} className={`flex items-center justify-between py-2 border-b border-navy/5 last:border-0 ${r.hl ? "font-medium" : ""}`}>
              <span className="text-sm text-navy/60">{r.label}</span>
              <span className={`text-sm ${r.hl ? "text-violet" : ""}`}>{r.value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RecordDetail({ appId, rec, schema, onClose, onStatus, onDelete, onSaved, projects }) {
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState(rec.notes || []);
  const [editing, setEditing] = useState(false);
  const [ef, setEf] = useState({ title: rec.title, description: rec.description || "", fields: rec.fields || {} });
  const [share, setShare] = useState(false);
  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState(rec.member_ids || []);
  const proj = projects.find((p) => p.id === rec.project_id);
  const fieldsDef = schema.fields || [];

  const addNote = async () => {
    if (!note.trim()) return;
    try { const r = await api.post(`/workspace/${appId}/records/${rec.id}/notes`, { text: note }); setNotes([...notes, r.data]); setNote(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const saveEdit = async () => {
    try { await api.patch(`/workspace/${appId}/records/${rec.id}`, { title: ef.title, description: ef.description, fields: ef.fields }); toast.success("Actualizado"); setEditing(false); onSaved(); onClose(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const openShare = async () => {
    setShare(true);
    try { const r = await api.get("/workspace/members"); setMembers(r.data); } catch { /* */ }
  };
  const saveShare = async () => {
    try { await api.put(`/workspace/${appId}/records/${rec.id}/members`, { member_ids: selected }); toast.success("Compartido"); setShare(false); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <Modal title={editing ? "Editar" : rec.title} onClose={onClose} testid="app-detail-dialog" wide>
      <div className="space-y-4">
        {editing ? (
          <div className="space-y-3">
            <div><label className="text-xs text-navy/50 mb-1 block">Nombre / Título</label><input className={input} value={ef.title} onChange={(e) => setEf({ ...ef, title: e.target.value })} /></div>
            {fieldsDef.length > 0 && <DynamicFields fields={fieldsDef} values={ef.fields} onChange={(fields) => setEf({ ...ef, fields })} />}
            <textarea className={input} rows={2} placeholder="Descripción" value={ef.description} onChange={(e) => setEf({ ...ef, description: e.target.value })} />
            <div className="flex justify-end gap-2"><button onClick={() => setEditing(false)} className="btn btn-ghost">Cancelar</button><button onClick={saveEdit} className="btn btn-primary" data-testid="app-detail-save">Guardar</button></div>
          </div>
        ) : (
          <>
            {rec.description && <p className="text-sm text-navy/60">{rec.description}</p>}
            {fieldsDef.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2" data-testid="app-detail-fields">
                {fieldsDef.map((fd) => (
                  <div key={fd.key} className="flex justify-between gap-3 py-1.5 border-b border-navy/5">
                    <span className="text-xs text-navy/45">{fd.label}</span>
                    <span className="text-sm text-navy/80 text-right">{fmtField(fd, rec.fields?.[fd.key])}</span>
                  </div>
                ))}
              </div>
            )}
            {proj && <div className="text-xs text-violet inline-flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> {proj.source}: {proj.name}</div>}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex-1 min-w-[160px]"><SelectField testid="app-detail-status" value={rec.status} options={STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] }))} onChange={(v) => onStatus(rec, v)} /></div>
              <span className={`badge ${PRIO_BADGE[rec.priority]}`}>{PRIO_LABEL[rec.priority]}</span>
              <button onClick={() => setEditing(true)} className="text-navy/40 hover:text-violet" data-testid="app-detail-edit" title="Editar"><Pencil className="w-4 h-4" /></button>
              <button onClick={openShare} className="text-navy/40 hover:text-violet" data-testid="app-detail-share" title="Compartir"><Users className="w-4 h-4" /></button>
              <button onClick={() => onDelete(rec.id)} className="text-navy/30 hover:text-destructive" data-testid="app-detail-delete" title="Eliminar"><Trash2 className="w-4 h-4" /></button>
            </div>
            <div>
              <div className="overline text-navy/40 mb-2 flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" /> Notas</div>
              <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                {notes.length === 0 ? <div className="text-xs text-navy/40">Sin notas.</div> : notes.map((n) => (
                  <div key={n.id} className="text-sm border-l-2 border-violet/30 pl-2"><span className="text-navy/70">{n.text}</span><span className="block text-[11px] text-navy/40">{n.by}</span></div>
                ))}
              </div>
              <div className="flex gap-2">
                <input className={input} placeholder="Agregar nota…" value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="app-note-input" />
                <button onClick={addNote} className="btn btn-outline text-sm" data-testid="app-note-add">Añadir</button>
              </div>
            </div>
          </>
        )}
      </div>

      {share && (
        <div className="fixed inset-0 z-[60] bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="app-share-dialog" onClick={() => setShare(false)}>
          <div className="card-surface w-full max-w-sm p-6 max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-xl">Compartir</h3><button onClick={() => setShare(false)}><X className="w-5 h-5 text-navy/40" /></button></div>
            <p className="text-xs text-navy/50 mb-3">Invita a colegas para que vean y colaboren en este registro.</p>
            <div className="space-y-1 mb-4">
              {members.length === 0 ? <div className="text-sm text-navy/40">No hay más usuarios en tu organización.</div> : members.map((m) => {
                const on = selected.includes(m.id);
                return (
                  <button key={m.id} onClick={() => setSelected(on ? selected.filter((x) => x !== m.id) : [...selected, m.id])} data-testid={`share-user-${m.id}`}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-left text-sm transition-colors ${on ? "bg-violet/10 text-violet" : "hover:bg-navy/5"}`}>
                    <span><span className="font-medium">{m.name}</span><span className="block text-[11px] text-navy/40">{m.email}</span></span>
                    {on && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
            <button onClick={saveShare} className="btn btn-primary w-full" data-testid="app-share-save">Guardar</button>
          </div>
        </div>
      )}
    </Modal>
  );
}
